import React from 'react';
import GenericCard from './GenericCard';
import PLUGINS from './plugins-manifest';
import SandboxedCard from './sandbox/SandboxedCard';
import { FACE_KIND } from './sandbox/faceKind';
import StationFace from './sandbox/StationFace';
import useFaceReduceMotion from './sandbox/faceMotion';
import { cardIndexEntry, entryNeedsChannel } from './sandbox/cardIndex';
import { isContinueAction, runContinueAction, withoutHiddenContinue } from './continueAt';
import { lazyView } from '../lazyView';

// Canvas (with DocumentView and its geometry) is the biggest card; load it on first use.
const CanvasCard = lazyView(() => import('./CanvasCard'));

// Card resolution has two tiers with different trust (ADR 2026-07-24-card-sandbox):
//
//   1. BUILD-TIME (`PLUGINS`) — cards AVEQ compiles into this bundle
//      (qlink/cards, aveq-aggregator/cards, aveq-providers/<m>/cards). Deployment-
//      trusted (same party as the origin); rendered inline, unsandboxed.
//   2. RUNTIME — cards shipped inside an installed/pulled helper package, served by
//      aveq-agent (/apps/cards.json → {url, function_sets}). THIRD-PARTY by the
//      pipeline (installer → standalone worker → publish), so rendered in an
//      opaque-origin sandboxed iframe (SandboxedCard). Supersedes the old
//      `<script>`-injection runtime path (runtime.js) — kept in-tree until the
//      sandbox is live-verified, then removed for pulled cards.
//
// GenericCard (Universal Card) is the fallback: no `kind`, an unknown kind, or
// while a runtime entry resolves.

// Cards that ship WITH the app, resolved before anything else. Canvas (ADR
// 2026-09-05-canvas-card) is deliberately not a PLUGINS entry: that manifest is generated from
// compiled per-app card bundles, which is the delivery chain Canvas exists to remove. A
// built-in kind cannot be shadowed by a runtime entry of the same name, which is also why it is
// checked first.
const BUILT_IN = { canvas: CanvasCard };

// gallery-efficiency: does a re-invoke embed need to open its action/station
// channel (the embed WS) for this CARD? FAIL-SAFE — keep the channel unless the
// card provably can't drive an action over it. Takes the card OBJECT (not just the
// kind) because a kind-LESS card is a real, actable shape (below). Every renderer
// that reaches `onAction → CardViewer.handleAction → sendAction` must keep it:
//   • build-time (PLUGINS) inline cards — tobin.restaurant_actions, presto.control,
//     tobin.dish/menu — act via this WS (they never hit SandboxedCard's qlink);
//   • kind-LESS GenericCard/Universal cards — e.g. JobSearch's find_jobs emits
//     {text, action:{label:'Apply'}} with NO kind; the Apply button drives this WS.
//     Such a card needs it iff it renders an action button (a pure-text card can't
//     act) — mirror GenericCard's own `actions ?? [action]` normalization;
//   • a kind we can't resolve (unknown / index-fetch flake) — keep it, don't
//     silently disable a card that might act.
// Only a sandboxed card the index resolves as `display` (or derived-display) is
// dropped. Mirrors the SandboxedCard qlink gate but on the OTHER socket; closes
// the build-time- and kind-less-actable-embed regressions (!500 review). See ADR
// 2026-08-13-gallery-efficiency.
export async function embedCardNeedsChannel(card) {
  if (!card) return false;
  const kind = card.kind;
  if (!kind) {
    const acts = card.actions ?? (card.action ? [card.action] : []);
    // kind-less GenericCard: acts iff a button that reaches the station (Continue doesn't)
    return Array.isArray(acts) && acts.some((a) => !isContinueAction(a));
  }
  if (BUILT_IN[kind] || PLUGINS[kind]) return true;   // built-in / build-time inline card
                                             // acts via THIS channel, so keep it
  try {
    const entry = await cardIndexEntry(kind);
    if (!entry) return true;                 // unresolved kind → GenericCard → keep (fail-safe)
    return entryNeedsChannel(entry);         // sandboxed: interactive keeps, display drops
  } catch (e) {
    return true;                             // index flake → keep the channel, don't disable
  }
}

export function CardFor({ structured: given, onAction: dispatchAction, accountId, fillHeight, onSwipe,
                          liveEventId, hidden, onEnd, onActivity }) {
  // card-continue-action: the browser handles a built-in Continue; it never reaches a station.
  const structured = React.useMemo(() => withoutHiddenContinue(given), [given]);
  const onAction = React.useCallback((action, payload) => {
    if (isContinueAction(action)) {
      runContinueAction(action, payload || structured);
      return;
    }
    if (dispatchAction) dispatchAction(action, payload);
  }, [dispatchAction, structured]);
  // The sandbox host already matched a Continue to this payload; send this payload, not the card's.
  const onSandboxedAction = React.useCallback((action, payload) => {
    if (isContinueAction(action)) runContinueAction(action, structured);
    else if (dispatchAction) dispatchAction(action, payload);
  }, [dispatchAction, structured]);
  const kind = structured.kind;
  const build = BUILT_IN[kind] || PLUGINS[kind];
  // Runtime index entry for a kind with no build-time plugin. null = still
  // resolving OR no runtime card → GenericCard.
  const [entry, setEntry] = React.useState(null);

  React.useEffect(() => {
    if (build) return undefined;   // build-time card wins; no runtime lookup
    let alive = true;
    cardIndexEntry(kind).then((e) => { if (alive) setEntry(e); });
    return () => { alive = false; };
  }, [kind, build]);

  if (kind === FACE_KIND) {
    return <FramedFace structured={structured} onAction={onSandboxedAction} entry={entry}
                       accountId={accountId} />;
  }
  if (build) {
    const Card = build;
    return <Card structured={structured} onAction={onAction} />;
  }
  // canvas-card: an entry that names a renderer this build carries is rendered INLINE by that
  // component. It ships no code — the payload is data, which the renderer treats as data — so
  // there is nothing for a sandbox to contain, and wrapping it in an opaque-origin iframe would
  // cost it the action channel for no gain.
  if (entry && entry.renderer && BUILT_IN[entry.renderer]) {
    const Card = BUILT_IN[entry.renderer];
    return <Card structured={structured} onAction={onAction} />;
  }
  if (entry && entry.url) {
    return (
      <SandboxedCard
        structured={structured}
        onAction={onSandboxedAction}
        entry={entry}
        accountId={accountId}
        fillHeight={fillHeight}
        onSwipe={onSwipe}
        liveEventId={liveEventId}
        hidden={hidden}
        onEnd={onEnd}
        onActivity={onActivity}
      />
    );
  }
  return <GenericCard structured={structured} onAction={onAction} />;
}

// A station face always renders in the host's frame, at the frame's size, even before (or
// without) its card entry: the frame is what marks it as a face. Behind it the page may put a
// placeholder; a face that fails, or hasn't started after FACE_START_MS, leaves just that.
export const FACE_START_MS = 6000;
export const FACE_DRAW_MS = 1000;

function FramedFace({ structured, onAction, entry, accountId }) {
  const reduceMotion = useFaceReduceMotion();
  const [state, setState] = React.useState('loading');   // loading | ready | drawn | gone
  const onLoaded = React.useCallback(() => setState((st) => (st === 'gone' ? st : 'ready')), []);
  const onFailed = React.useCallback(() => setState('gone'), []);
  React.useEffect(() => {
    const t = setTimeout(() => setState((st) => (st === 'loading' ? 'gone' : st)), FACE_START_MS);
    return () => clearTimeout(t);
  }, []);
  // A face paints right after it starts; then the placeholder goes, so it can't show through.
  React.useEffect(() => {
    if (state !== 'ready') return undefined;
    const t = setTimeout(() => setState('drawn'), FACE_DRAW_MS);
    return () => clearTimeout(t);
  }, [state]);
  return (
    <StationFace structured={structured} covered={state === 'drawn'}>
      {state !== 'gone' && reduceMotion !== null && entry && entry.url && (
        <SandboxedCard structured={structured} onAction={onAction} entry={entry}
                       accountId={accountId} fillHeight reduceMotion={reduceMotion}
                       onLoaded={onLoaded} onFailed={onFailed} />
      )}
    </StationFace>
  );
}
